import { getFirstLastNameLetters } from '@/utils';
import { ReactElement, useMemo } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';
import { UserAvatarPlaceholder } from '../Icons';
import { Spinner } from '../Spinner';

export type AvatarSize = 'small' | 'medium' | 'medium-large' | 'large';

export interface UserAvatarProps {
  name?: string;
  size?: AvatarSize;
  className?: string;
  withOusideName?: boolean;
  isLoading?: boolean;
}

export const UserAvatar = ({
  name,
  size = 'medium',
  className,
  withOusideName,
  isLoading,
}: UserAvatarProps): ReactElement => {
  const styleInfo = useMemo(() => {
    if (size === 'small') {
      return 'size-6';
    }
    if (size === 'medium') {
      return 'size-11';
    }
    if (size === 'medium-large') {
      return 'size-16';
    }
    return 'size-20';
  }, [size]);

  const content = useMemo(() => {
    return name ? (
      <div className="font-heading flex h-full w-full items-center justify-center bg-gray-100 font-bold text-blue-500">
        <span
          className={twJoin(
            size === 'small' && 'text-sm',
            size === 'medium' && 'text-md',
            size === 'medium-large' && 'text-lg',
            size === 'large' && 'text-lg',
          )}
        >
          {getFirstLastNameLetters(name ?? '')}
        </span>
      </div>
    ) : (
      <UserAvatarPlaceholder className={styleInfo} />
    );
  }, [name, size, styleInfo]);

  return isLoading ? (
    <Spinner />
  ) : (
    <div className="flex items-center gap-[5px]">
      <div
        className={twMerge(
          'flex items-center justify-center overflow-hidden rounded-full ',
          styleInfo,
          className,
        )}
      >
        {content}
      </div>
      {withOusideName && name && (
        <span
          className={twJoin(
            'font-normal text-gray',
            size === 'small' && 'text-sm',
            size === 'medium' && 'text-md',
            size === 'medium-large' && 'text-lg',
            size === 'large' && 'text-lg',
          )}
        >
          {name}
        </span>
      )}
    </div>
  );
};
